"use client";
import React, { useState, useRef, useEffect } from "react";
import Image, { StaticImageData } from "next/image";
// PERF: GSAP + ScrollTrigger loaded lazily via ensureGsap() (below-the-fold
// pinned scroll animation; registerPlugin after mount). Behavior unchanged.
import { ensureGsap } from "@/lib/EnsureGsap";
import { Images } from "@assets/assets";

type StepItem = {
  title: string;
  image?: StaticImageData | string;
  description: string;
};

interface IndustryApproachProps {
  header?: {
    title: React.ReactNode;
    subtitle: string;
    paragraph?: string;
  };
  steps?: StepItem[];
  version?: "dark" | "light";
}

export default function IndustryApproachNew({
  header,
  steps = [],
  version = "dark",
}: IndustryApproachProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const isDark = version === "dark";
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();

        mm.add("(min-width: 1024px)", () => {
          ScrollTrigger.create({
            trigger: sectionRef.current,
            start: "top top",
            end: "+=3000",
            pin: true,
            scrub: 1,
            // This is the bottom-most pinned section on these tech pages, below
            // other lazy-loaded pins (e.g. IndustryProcess "BENEFITS"). They
            // register asynchronously, so ScrollTrigger can measure this one before
            // the spacers above exist and cache a wrong start -> full-screen white
            // gap. A lower refreshPriority forces it to recompute LAST, after the
            // higher pins have applied their spacing. (Pages render fine today, but
            // the ordering is load-timing dependent without this.)
            refreshPriority: -1,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const progress = self.progress;
              const newStep = Math.min(
                Math.floor(progress * steps.length),
                steps.length - 1,
              );
              setCurrentStep(newStep);
            },
          });
        });
      });

      cleanup = () => ctx.revert();
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [steps.length]);

  return (
    // Wrapper is React-owned; the pinned section sits INSIDE it so GSAP's
    // injected pin-spacer never displaces this component's root in the page
    // container's child list — otherwise a sibling section swapping its tree
    // makes React insertBefore against a node GSAP re-parented and throw
    // `NotFoundError`. See components/common/Solution/IndustryProcess.tsx.
    <div className="w-full">
      <section
        ref={sectionRef}
        className={`relative w-full min-h-screen overflow-hidden flex flex-col py-10 lg:py-20 ${
          isDark ? "bg-[#02050a] text-white" : "bg-white text-black"
        }`}
      >
        {/* --- Restored Background Image/Texture --- */}
        <div
          className={`texture-bg absolute inset-0 w-full h-full z-0 pointer-events-none ${
            isDark ? "mix-blend-overlay opacity-100" : "opacity-10"
          }`}
        >
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        </div>
        {/* Decorative Blur Gradients (Optional, common in these designs) */}
        {isDark && (
          <>
            <div className="w-[500px] h-[500px] bg-[#225bd4]/10 rounded-full blur-[120px] absolute -top-24 -right-24 z-0 pointer-events-none" />
            <div className="w-[500px] h-[500px] bg-[#225bd4]/10 rounded-full blur-[120px] absolute -bottom-24 -left-24 z-0 pointer-events-none" />
          </>
        )}

        <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] z-10 flex flex-col h-full relative">
          {/* --- Header Block --- */}
          <div className="mb-[60px] lg:mb-[100px]">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-10">
              <div className="lg:w-8/12">
                <span
                  className={`uppercase tracking-[0.1em] text-[13px] mb-6 block font-medium ${isDark ? "text-white" : "text-black/60"}`}
                >
                  {header?.subtitle}
                </span>
                <h2
                  className={`clash-font text-[length:var(--section-title)] leading-[1.05] font-medium ${isDark ? "text-white" : "text-black"}`}
                >
                  {header?.title}
                </h2>
              </div>

              <div className="lg:w-4/12 lg:pt-[30px]">
                <p
                  className={`text-[18px] leading-[1.6] font-light lg:text-right ${isDark ? "text-white/70" : "text-black/70"}`}
                >
                  {header?.paragraph}
                </p>
              </div>
            </div>
          </div>

          {/* --- Desktop View (3-8-1 Grid) --- */}
          <div className="hidden xl:grid grid-cols-12 gap-8 items-center flex-1 pb-10">
            <div className="col-span-3 flex flex-col gap-10">
              {steps?.map((step, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-6 transition-all duration-500 ease-out ${
                    currentStep === idx
                      ? "opacity-100 translate-x-3"
                      : "opacity-20 translate-x-0"
                  }`}
                >
                  <span className="text-lg font-medium pt-1 clash-font">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-xl font-medium leading-tight max-w-[200px] clash-font">
                    {step.title}
                  </h3>
                </div>
              ))}
            </div>

            <div className="col-span-8">
              <div
                className={`relative flex flex-col lg:flex-row items-center p-10 lg:p-14 ${
                  isDark
                    ? "bg-[#0b0f17] border border-white/5 shadow-2xl"
                    : "bg-gray-50 border border-black/5"
                } min-h-[450px]`}
              >
                <div className="flex-1 lg:pr-12">
                  <h3 className="text-4xl lg:text-5xl font-semibold mb-6 clash-font">
                    {steps[currentStep]?.title}
                  </h3>
                  <p
                    className={`text-lg leading-relaxed ${isDark ? "text-white/50" : "text-black/60"}`}
                  >
                    {steps[currentStep]?.description}
                  </p>
                </div>

                <div className="w-full lg:w-[35%] aspect-square relative overflow-hidden mt-8 lg:mt-0 rounded-sm">
                  {steps[currentStep]?.image && (
                    <Image
                      src={steps[currentStep].image!}
                      alt={steps[currentStep].title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="col-span-1 flex flex-col items-center gap-4">
              {steps?.map((_, idx) => (
                <div
                  key={idx}
                  className={`rounded-full transition-all duration-300 ${
                    currentStep === idx
                      ? "w-2.5 h-2.5 bg-white scale-125 shadow-[0_0_8px_rgba(255,255,255,0.5)]"
                      : "w-1.5 h-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* --- Mobile View (Stacked) --- */}
          <div className="xl:hidden flex flex-col gap-8">
            {steps?.map((step, idx) => (
              <div
                key={idx}
                className={`p-8 rounded-xl flex flex-col gap-6 relative overflow-hidden ${
                  isDark
                    ? "bg-[#0b0f17] border border-white/5"
                    : "bg-gray-50 border border-black/5"
                }`}
              >
                <div className="flex items-center gap-4 relative z-10">
                  <span className="text-lg font-medium opacity-40 clash-font">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-2xl font-semibold clash-font">
                    {step.title}
                  </h3>
                </div>

                {step.image && (
                  <div className="w-full aspect-[16/10] relative rounded-lg overflow-hidden z-10">
                    <Image
                      src={step.image}
                      alt={step.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  </div>
                )}

                <p
                  className={`text-lg leading-relaxed relative z-10 ${isDark ? "text-white/60" : "text-black/70"}`}
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
